import axios from "axios";

let BASE_URL =
  process.env.REACT_APP_ENVIRONMENT === "production"
    ? "https://api.chipcount.xyz"
    : "http://localhost:5000";

/* Auto-logout on auth failures or repeated API errors                  */
let failures = 0;
let redirecting = false;

const forceLogout = (reason) => {
  if (redirecting) return;
  redirecting = true;
  localStorage.removeItem("token");
  localStorage.removeItem("googleToken");
  window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
};

axios.interceptors.response.use(
  (response) => {
    failures = 0; // any success resets the counter
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";

    // Don't interfere with login/register/logout flows
    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/google") ||
      url.includes("/auth/logout");

    if (!isAuthRoute && (status === 401 || status === 403)) {
      forceLogout("Your session has expired. Please log in again.");
      return Promise.reject(error);
    }

    // Network errors or server errors — count consecutive failures
    if (!isAuthRoute && (!status || status >= 500)) {
      failures += 1;
      if (failures >= 3) {
        failures = 0;
        forceLogout(
          "We're having trouble connecting. Please log in again."
        );
      }
    }

    return Promise.reject(error);
  }
);

const API = {
  // Auth endpoints
  registerUser: (payload) => {
    const url = `${BASE_URL}/auth/register`;
    return axios.post(url, payload);
  },
  registerUserWithGoogle: (payload) => {
    const url = `${BASE_URL}/auth/register-google`;
    return axios.post(url, payload);
  },
  googleLogin: (payload) => {
    const url = `${BASE_URL}/auth/google`;
    return axios.post(url, payload);
  },
  userLogin: (payload) => {
    const url = `${BASE_URL}/auth/login`;
    return axios.post(url, payload);
  },
  logout: (token, googleToken) => {
    const url = `${BASE_URL}/auth/logout`;
    const headers = {
      "Content-Type": "application/json",
      token,
      ...(googleToken && { googleToken }),
    };
    return axios.post(url, {}, { headers });
  },

  // Game endpoints
  getGameDetailsFromGameId: (gameId, token) => {
    const url = `${BASE_URL}/games/${gameId}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getGameSettlementDetailsFromGameId: (gameId, token) => {
    const url = `${BASE_URL}/games/settlement/${gameId}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  updateGameDetailsForGameId: (gameId, payload, token) => {
    const url = `${BASE_URL}/games/${gameId}`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  endGame: (gameId, payload, token) => {
    const url = `${BASE_URL}/games/${gameId}/end`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  deleteGame: (gameId, token) => {
    const url = `${BASE_URL}/games/${gameId}`;
    return axios.delete(url, {
      headers: { token },
    });
  },
  createGame: (payload, token) => {
    const url = `${BASE_URL}/games`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },

  // Player endpoints
  searchRoomUsers: (query, token) => {
    const url = `${BASE_URL}/games/search-users?query=${encodeURIComponent(
      query
    )}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  addPlayerToGame: (payload, token) => {
    const url = `${BASE_URL}/games/add-players`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },
  updatePlayerDetails: (gameId, playerId, payload, token) => {
    const url = `${BASE_URL}/games/${gameId}/players/${playerId}`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  topUpPlayer: (gameId, playerId, payload, token) => {
    const url = `${BASE_URL}/games/${gameId}/players/${playerId}/topup`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },
  chipOutPlayer: (gameId, playerId, payload, token) => {
    const url = `${BASE_URL}/games/${gameId}/players/${playerId}/chipout`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },
  deletePlayer: (gameId, playerId, token) => {
    const url = `${BASE_URL}/games/${gameId}/players/${playerId}`;
    return axios.delete(url, {
      headers: { token },
    });
  },

  // History and logs
  getGamesList: (queryParams, token) => {
    const url = `${BASE_URL}/games?${queryParams}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getPlayerHistory: (gameId, playerId, token) => {
    const url = `${BASE_URL}/games/${gameId}/player-actions/${playerId}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getGameHistory: (gameId, token) => {
    const url = `${BASE_URL}/games/${gameId}/game-actions`;
    return axios.get(url, {
      headers: { token },
    });
  },

  // Room endpoints
  getUserRoomsList: (token) => {
    const url = `${BASE_URL}/room/user-rooms`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getRoomDetailsFromRoomId: (roomId, token) => {
    const url = `${BASE_URL}/room/rooms/${roomId}`;
    return axios.get(url, {
      headers: { token },
    });
  },
  updateRoomDetailsFromRoomId: (roomId, payload, token) => {
    const url = `${BASE_URL}/room/rooms/${roomId}`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  createRoom: (payload, token) => {
    const url = `${BASE_URL}/room/rooms`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },
  deleteRoom: (roomId, token) => {
    const url = `${BASE_URL}/room/rooms/${roomId}`;
    return axios.delete(url, {
      headers: { token },
    });
  },

  // Dashboard and user profile
  getDashboardData: (token) => {
    const url = `${BASE_URL}/games/dashboard-data`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getUserProfile: (token) => {
    const url = `${BASE_URL}/user/profile`;
    return axios.get(url, {
      headers: { token },
    });
  },
  getGameDefaults: (token) => {
    const url = `${BASE_URL}/user/game-defaults`;
    return axios.get(url, {
      headers: { token },
    });
  },
  updateGameDefaults: (payload, token) => {
    const url = `${BASE_URL}/user/game-defaults`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  updateUserProfile: (payload, token) => {
    const url = `${BASE_URL}/user/profile`;
    return axios.put(url, payload, {
      headers: { token },
    });
  },
  addFeedback: (payload, token) => {
    const url = `${BASE_URL}/user/feedback`;
    return axios.post(url, payload, {
      headers: { token },
    });
  },
};

export { API };
